import { Image, Input, Table, TableColumnsType } from "antd";
import { Link } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import AddIcon from "components/Icons/AddIcon";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import {
  sortIcon,
  statusBadge,
  verificationStatusBadge,
} from "components/Table";
import Action from "./components/Action";
import { RouteName } from "constants/route";
import useDriver from "hooks/drivers/useDriver";
import { IDriver } from "models/driver";
import { DriverAlertContextProvider } from "./components/AlertContext";
import { isCasher } from "helpers/privilege";
import { Tabs } from "antd";
import DriverRankings from "../DriverRankings";

export default function Driver() {
  const {
    data: { data, loading, page, per_page, search, total },
    handleSearch,
    handlePageChange,
  } = useDriver();

  return (
    <DriverAlertContextProvider>
      <div className="w-full">
        <Tabs
          defaultActiveKey="list"
          items={[
            {
              key: "list",
              label: "Drivers List",
              children: (
                <>
                  <div className="w-full px-6 flex flex-wrap-reverse">
                    <div className="grow justify-between items-center flex-wrap-reverse gap-5">
                      {isCasher() && (
                        <Link
                          to={RouteName.CREATE_DRIVER}
                          className="flex items-center p-3 border rounded-lg border-black w-fit gap-x-2"
                        >
                          <AddIcon />
                          <span className="text-black text-sm font-medium">
                            Add Driver
                          </span>
                        </Link>
                      )}
                    </div>
                    <Input
                      className="max-w-72 w-full h-auto px-4 py-2 text-base border border-[rgb(0,0,0,0.1)] rounded-3xl"
                      prefix={<SearchOutlined className="mr-2" />}
                      placeholder="Search..."
                      onChange={handleSearch}
                      value={search}
                    />
                  </div>
                  <div className="w-full mt-8 px-4">
                    <Table
                      loading={loading}
                      columns={columns(page, per_page)}
                      dataSource={data}
                      onChange={handlePageChange}
                      pagination={{
                        total,
                        pageSize: per_page,
                        current: page,
                        nextIcon: (
                          <span className="flex items-center justify-center w-8 h-8 text-2xl">
                            <NextIcon />
                          </span>
                        ),
                        prevIcon: (
                          <span className="flex items-center justify-center w-8 h-8 text-2xl">
                            <PreviousIcon />
                          </span>
                        ),
                      }}
                    />
                  </div>
                </>
              ),
            },
            {
              key: "rankings",
              label: "Daily Rankings",
              children: <DriverRankings />,
            },
          ]}
        />
      </div>
    </DriverAlertContextProvider>
  );
}

const columns = (page: number, perPage: number): TableColumnsType<IDriver> => [
  {
    title: (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        No
        {/* <div style={{ display: "flex", flexDirection: "column" }}>
          <Button
            type="text"
            size="small"
            icon={<CaretUpOutlined />}
            className="h-[12px]"
          />
          <Button
            type="text"
            size="small"
            icon={<CaretDownOutlined />}
            className="h-[12px]"
          />
        </div> */}
      </div>
    ),
    render: (_, __, index) => {
      // Calculate base number based on pagination
      let number = index + 1 + (page - 1) * perPage;

      // Apply sorting if needed
      // if (sortDirection === "desc") {
      //   number = dataSource.length - index + (page - 1) * perPage;
      // }

      return <p className="text-base font-medium">{number}</p>;
    },
  },
  {
    title: "Driver Photo",
    dataIndex: "image",
    render: (image: string) => (
      <Image
        height={60}
        width={60}
        className="rounded object-cover"
        src={image}
        preview={false}
      />
    ),
  },
  {
    title: "Driver Name",
    sortIcon,
    sorter: (a, b) =>
      `${a.first_name} ${a.middle_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.middle_name} ${b.last_name}`
      ),
    render: (value: IDriver) => (
      <p>{`${value.first_name} ${value.middle_name} ${value.last_name}`}</p>
    ),
  },
  {
    title: "Phone Number",
    dataIndex: "phone",
    sortIcon,
    sorter: (a, b) => a.phone.localeCompare(b.phone),
  },
  {
    title: "Registered By",
    sortIcon,
    sorter: (a, b) =>
      `${a.account?.first_name} ${a.account?.last_name}`.localeCompare(
        `${b.account?.first_name} ${b.account?.last_name}`
      ),
    render: (value: IDriver) => (
      <p>
        {value?.account
          ? `${value.account.first_name} ${value.account.last_name}`
          : "Registered By App"}
      </p>
    ),
  },
  {
    title: "Verified",
    dataIndex: "is_verified",
    sortIcon,
    sorter: (a, b) =>
      a.is_verified === b.is_verified ? 0 : a.is_verified ? -1 : 1,
    render: verificationStatusBadge,
  },
  {
    title: "Active",
    dataIndex: "is_active",
    sortIcon,
    sorter: (a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1),
    render: statusBadge,
  },
  {
    title: "Action",
    render: (value: IDriver) => <Action driver={value} />,
  },
];
